import React, { useState, useCallback, useMemo } from 'react'
import { Box, Button, TextField, MenuItem, Select } from '@material-ui/core'
import { useStyles } from './styles'
import {
  SortOrder,
  DATE_KEY_DEFAULT,
  MONTH_KEY_DEFAULT,
  ALL_ACCOUNTS_LABEL,
  CSV_PER_SITE_PREFIX,
  CSV_GROUPED_PREFIX,
  SITES_CHUNK_SIZE,
  GRAND_TOTAL_LABEL,
  STAT_LABELS,
  SORT_KEYS,
  EMPTY_TABLE_TEXT,
  FETCH_ERROR_MESSAGE,
} from './constants'
import { getErrorMessage } from '../constants'
import TableData from '../TableData'
import Search from '../../../../elements/icons/Search'
import Download from '../../../../elements/icons/Download'
import { DisplayMessage, Loader } from '../../../../Components'
import { eventService } from '../../../../Services/EventService'
import { connect } from 'react-redux'
import * as messageTypes from '../../../../utils/messageTypes'

const formatDateKey = (rawDate) => {
  if (!rawDate) return rawDate
  if (rawDate.length === 8) return `${rawDate.slice(0, 4)}-${rawDate.slice(4, 6)}-${rawDate.slice(6, 8)}`
  if (rawDate.length === 6) return `${rawDate.slice(0, 4)}-${rawDate.slice(4, 6)}`
  return rawDate
}

const escapeCsvValue = (value) => {
  const stringValue = value == null ? '' : String(value)
  return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue
}

const SiteIdsViewer = () => {
  const classes = useStyles()

  const [allData, setAllData] = useState([])
  const [dateKey, setDateKey] = useState(DATE_KEY_DEFAULT)
  const [monthKey, setMonthKey] = useState(MONTH_KEY_DEFAULT)
  const [searchTerm, setSearchTerm] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [sortBy, setSortBy] = useState(null)
  const [sortOrder, setSortOrder] = useState(SortOrder.ASC)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const processResponse = useCallback((response) => {
    const programs = response.data ?? []
    setAllData(programs)
    setSortBy(null)
    setSortOrder(SortOrder.ASC)
    setAccountFilter('')
    setSearchTerm('')
    setPage(0)
  }, [])

  const fetchData = () => {
    const params = {}
    const trimmedDateKey = dateKey.trim()
    const trimmedMonthKey = monthKey.trim()
    if (trimmedMonthKey) {
      params.month_key_for_max_active = trimmedMonthKey
    } else if (trimmedDateKey) {
      params.date_key = trimmedDateKey
    }

    if (!trimmedDateKey && !trimmedMonthKey) {
      setMessage('Please enter a date_key or month_key_for_max_active')
      setMessageType(messageTypes.ERROR)
      setOpen(true)
      return
    }

    setLoading(true)
    eventService.getActiveSiteIds(params)
      .then(response => {
        processResponse(response)
        setMessage(`Loaded ${response.data?.length ?? 0} program(s)`)
        setMessageType(messageTypes.SUCCESS)
        setOpen(true)
      })
      .catch(error => {
        setMessage(getErrorMessage(error, FETCH_ERROR_MESSAGE))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => setLoading(false))
  }

  const filteredData = useMemo(() => {
    let rows = allData
    if (accountFilter) {
      rows = rows.filter(program => program.accountName === accountFilter)
    }
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase()
      rows = rows.filter(program =>
        (program.programNameAdminUI || '').toLowerCase().includes(lowerSearch) ||
        (program.accountName || '').toLowerCase().includes(lowerSearch) ||
        (program.programKey || '').toLowerCase().includes(lowerSearch)
      )
    }
    return rows
  }, [allData, accountFilter, searchTerm])

  const sortedData = useMemo(() => {
    if (sortBy === null) return filteredData
    return [...filteredData].sort((firstProgram, secondProgram) => {
      let firstValue, secondValue
      switch (sortBy) {
        case SORT_KEYS.PROGRAM:
          firstValue = (firstProgram.programNameAdminUI || '').toLowerCase()
          secondValue = (secondProgram.programNameAdminUI || '').toLowerCase()
          break
        case SORT_KEYS.ACCOUNT:
          firstValue = (firstProgram.accountName || '').toLowerCase()
          secondValue = (secondProgram.accountName || '').toLowerCase()
          break
        case SORT_KEYS.DATE:
          firstValue = firstProgram.activeAsOnDate || ''
          secondValue = secondProgram.activeAsOnDate || ''
          break
        case SORT_KEYS.COUNT:
          firstValue = (firstProgram.sites || []).length
          secondValue = (secondProgram.sites || []).length
          break
        case SORT_KEYS.NO_BATTERY:
          firstValue = firstProgram.sitesWithoutBattery ? firstProgram.sitesWithoutBattery.length : -1
          secondValue = secondProgram.sitesWithoutBattery ? secondProgram.sitesWithoutBattery.length : -1
          break
        default:
          return 0
      }
      if (firstValue < secondValue) return sortOrder === SortOrder.ASC ? -1 : 1
      if (firstValue > secondValue) return sortOrder === SortOrder.ASC ? 1 : -1
      return 0
    })
  }, [filteredData, sortBy, sortOrder])

  const handleSortChange = (columnId, direction) => {
    setSortBy(columnId)
    setSortOrder(direction)
  }

  // Compute stats from filtered data
  const stats = useMemo(() => {
    let totalSites = 0
    let totalNoBattery = 0
    const dates = new Set()
    filteredData.forEach(program => {
      totalSites += (program.sites || []).length
      if (program.sitesWithoutBattery) totalNoBattery += program.sitesWithoutBattery.length
      if (program.activeAsOnDate) dates.add(program.activeAsOnDate)
    })
    return {
      programs: filteredData.length,
      totalSites,
      totalNoBattery,
      dates: [...dates].map(formatDateKey).join(', '),
    }
  }, [filteredData])

  // Build table headings
  const tableHeadings = [
    { id: 'index', label: '#', sortOrder: null },
    { id: SORT_KEYS.PROGRAM, label: 'Program', sortOrder: sortBy === SORT_KEYS.PROGRAM ? sortOrder : SortOrder.ASC },
    { id: SORT_KEYS.ACCOUNT, label: 'Account', sortOrder: sortBy === SORT_KEYS.ACCOUNT ? sortOrder : SortOrder.ASC },
    { id: SORT_KEYS.DATE, label: 'Active As On Date', sortOrder: sortBy === SORT_KEYS.DATE ? sortOrder : SortOrder.ASC },
    { id: SORT_KEYS.COUNT, label: 'Site Count', sortOrder: sortBy === SORT_KEYS.COUNT ? sortOrder : SortOrder.ASC },
    { id: 'sites', label: 'Sites (comma-separated)', sortOrder: null },
    { id: SORT_KEYS.NO_BATTERY, label: 'No-Battery Count', sortOrder: sortBy === SORT_KEYS.NO_BATTERY ? sortOrder : SortOrder.ASC },
    { id: 'noBatterySites', label: 'Sites Without Battery', sortOrder: null },
  ]

  // Build table contents
  const tableContents = sortedData.map((program, index) => ({
    ...program,
    id: program.programId || index,
    _idx: index + 1,
    _isGrandTotal: false,
  }))
  if (sortedData.length > 0) {
    const totalSites = sortedData.reduce((sum, program) => sum + (program.sites || []).length, 0)
    const totalNoBattery = sortedData.reduce((sum, program) => sum + (program.sitesWithoutBattery ? program.sitesWithoutBattery.length : 0), 0)
    tableContents.push({
      id: '__grand_total__',
      _isGrandTotal: true,
      _count: sortedData.length,
      _totalSites: totalSites,
      _totalNoBattery: totalNoBattery,
    })
  }

  const renderCellContent = (item, heading) => {
    if (item._isGrandTotal) {
      if (heading.id === 'index') return ''
      if (heading.id === SORT_KEYS.PROGRAM || heading.id === SORT_KEYS.ACCOUNT || heading.id === SORT_KEYS.DATE) {
        if (heading.id === SORT_KEYS.DATE) return `${GRAND_TOTAL_LABEL} (${item._count} programs)`
        return ''
      }
      if (heading.id === SORT_KEYS.COUNT) return `${item._totalSites.toLocaleString()} sites`
      if (heading.id === 'sites') return ''
      if (heading.id === SORT_KEYS.NO_BATTERY) return item._totalNoBattery > 0 ? item._totalNoBattery.toLocaleString() : ''
      if (heading.id === 'noBatterySites') return ''
      return ''
    }

    if (heading.id === 'index') return item._idx

    if (heading.id === SORT_KEYS.PROGRAM) {
      return (
        <Box>
          <Box className={classes.programName}>{item.programNameAdminUI}</Box>
          <Box className={classes.programKey}>{item.programKey}</Box>
        </Box>
      )
    }

    if (heading.id === SORT_KEYS.ACCOUNT) {
      return <Box component="span" className={classes.accountBadge}>{item.accountName}</Box>
    }

    if (heading.id === SORT_KEYS.DATE) {
      return <Box component="span" className={classes.dateBadge}>{formatDateKey(item.activeAsOnDate)}</Box>
    }

    if (heading.id === SORT_KEYS.COUNT) {
      const count = (item.sites || []).length
      return (
        <Box component="span" className={`${classes.countBadge} ${count === 0 ? classes.countZero : ''}`}>
          {count.toLocaleString()}
        </Box>
      )
    }

    if (heading.id === 'sites') {
      const sites = item.sites || []
      if (sites.length === 0) {
        return (
          <Box
            component="textarea"
            readOnly
            value="No sites"
            className={`${classes.sitesTextarea} ${classes.sitesTextareaEmpty}`}
          />
        )
      }
      return (
        <Box
          component="textarea"
          readOnly
          value={sites.join(', ')}
          className={classes.sitesTextarea}
        />
      )
    }

    if (heading.id === SORT_KEYS.NO_BATTERY) {
      if (!item.sitesWithoutBattery) {
        return <Box component="span" className={classes.noBatteryNull}>&mdash;</Box>
      }
      return (
        <Box component="span" className={classes.noBatteryBadge}>
          {item.sitesWithoutBattery.length.toLocaleString()}
        </Box>
      )
    }

    if (heading.id === 'noBatterySites') {
      if (!item.sitesWithoutBattery) {
        return <Box component="span" className={classes.noBatteryNull}>&mdash;</Box>
      }
      if (item.sitesWithoutBattery.length === 0) {
        return (
          <Box
            component="textarea"
            readOnly
            value="None"
            className={`${classes.noBatterySitesTextarea} ${classes.noBatterySitesEmpty}`}
          />
        )
      }
      return (
        <Box
          component="textarea"
          readOnly
          value={item.sitesWithoutBattery.join(', ')}
          className={classes.noBatterySitesTextarea}
        />
      )
    }

    return ''
  }

  const getCellClassName = (item, heading) => {
    if (item._isGrandTotal) {
      if (heading.id === SORT_KEYS.DATE) return classes.grandTotalLabel
      return classes.grandTotalCell
    }
    if (heading.id === 'index') return classes.indexCell
    if (heading.id === SORT_KEYS.COUNT || heading.id === SORT_KEYS.NO_BATTERY) return classes.numCell
    return classes.titleWrapper
  }

  // CSV export - per site (one row per site ID, denormalized)
  const exportPerSiteCsv = () => {
    if (!filteredData.length) return
    const header = ['Program Name', 'Program Key', 'Account Name', 'Program ID', 'Account ID', 'Active As On Date', 'Site ID'].map(escapeCsvValue).join(',')
    const csvRows = []
    filteredData.forEach(program => {
      const sites = program.sites || []
      if (sites.length === 0) {
        csvRows.push([
          escapeCsvValue(program.programNameAdminUI ?? ''), escapeCsvValue(program.programKey ?? ''),
          escapeCsvValue(program.accountName ?? ''), program.programId ?? '', program.accountId ?? '',
          program.activeAsOnDate ?? '', '',
        ].join(','))
      } else {
        sites.forEach(siteId => {
          csvRows.push([
            escapeCsvValue(program.programNameAdminUI ?? ''), escapeCsvValue(program.programKey ?? ''),
            escapeCsvValue(program.accountName ?? ''), program.programId ?? '', program.accountId ?? '',
            program.activeAsOnDate ?? '', siteId,
          ].join(','))
        })
      }
    })
    const csvContent = [header, ...csvRows].join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `${CSV_PER_SITE_PREFIX}_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(downloadUrl)
  }

  // CSV export - grouped (sites chunked into groups of SITES_CHUNK_SIZE per row)
  const exportGroupedCsv = () => {
    if (!filteredData.length) return
    const header = [
      'Program Name', 'Program Key', 'Account Name', 'Program ID', 'Account ID',
      'Active As On Date', 'Total Site Count', 'No-Battery Count', 'Part', 'Sites', 'Sites Without Battery',
    ].map(escapeCsvValue).join(',')

    const csvRows = []
    filteredData.forEach(program => {
      const sites = program.sites || []
      const noBatterySites = program.sitesWithoutBattery || []
      const siteChunks = []
      for (let offset = 0; offset < sites.length; offset += SITES_CHUNK_SIZE) {
        siteChunks.push(sites.slice(offset, offset + SITES_CHUNK_SIZE))
      }
      const noBatteryChunks = []
      for (let offset = 0; offset < noBatterySites.length; offset += SITES_CHUNK_SIZE) {
        noBatteryChunks.push(noBatterySites.slice(offset, offset + SITES_CHUNK_SIZE))
      }
      const maxParts = Math.max(siteChunks.length, noBatteryChunks.length, 1)
      for (let partIndex = 0; partIndex < maxParts; partIndex++) {
        csvRows.push([
          escapeCsvValue(program.programNameAdminUI ?? ''), escapeCsvValue(program.programKey ?? ''),
          escapeCsvValue(program.accountName ?? ''), program.programId ?? '', program.accountId ?? '',
          program.activeAsOnDate ?? '',
          partIndex === 0 ? sites.length : '',
          partIndex === 0 ? (program.sitesWithoutBattery ? noBatterySites.length : '') : '',
          maxParts > 1 ? (partIndex + 1) : '',
          escapeCsvValue((siteChunks[partIndex] || []).join(', ')),
          escapeCsvValue((noBatteryChunks[partIndex] || []).join(', ')),
        ].join(','))
      }
    })
    const csvContent = [header, ...csvRows].join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `${CSV_GROUPED_PREFIX}_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(downloadUrl)
  }

  const accountNames = useMemo(() =>
    [...new Set(allData.map(program => program.accountName).filter(Boolean))].sort(),
    [allData]
  )

  return (
    <Box>
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <Loader loading={loading} />

      <Box className={classes.urlBar}>
        <Box className={classes.urlRow}>
          <Box className={classes.urlBarLabel}>date_key</Box>
          <TextField
            className={classes.urlInput}
            value={dateKey}
            onChange={(e) => setDateKey(e.target.value)}
            placeholder="yyyyMMdd"
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
          <Box className={classes.urlBarLabel}>month_key_for_max_active</Box>
          <TextField
            className={classes.urlInput}
            value={monthKey}
            onChange={(e) => setMonthKey(e.target.value)}
            placeholder="yyyyMM"
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
          <Button className={classes.submitBtn} onClick={fetchData} disabled={loading}>
            <Search />
            <Box paddingLeft='10px'>Fetch & Render</Box>
          </Button>
        </Box>
      </Box>

      {allData.length > 0 && (
        <Box className={classes.statsBar}>
          <Box component="span" className={classes.statPill}>
            <Box component="span" className={classes.statPillStrong}>{stats.programs.toLocaleString()}</Box>&nbsp;{STAT_LABELS.PROGRAMS}
          </Box>
          <Box component="span" className={`${classes.statPill} ${classes.statPillGreen}`}>
            {STAT_LABELS.TOTAL_SITES}: <Box component="span" className={classes.statPillStrong}>{stats.totalSites.toLocaleString()}</Box>
          </Box>
          <Box component="span" className={`${classes.statPill} ${classes.statPillAmber}`}>
            {STAT_LABELS.AS_ON_DATE}: <Box component="span" className={classes.statPillStrong}>{stats.dates}</Box>
          </Box>
          {stats.totalNoBattery > 0 && (
            <Box component="span" className={`${classes.statPill} ${classes.statPillAmber}`}>
              {STAT_LABELS.WITHOUT_BATTERY}: <Box component="span" className={classes.statPillStrong}>{stats.totalNoBattery.toLocaleString()}</Box>
            </Box>
          )}
        </Box>
      )}

      <Box className={classes.container}>
        <Box className={classes.card}>
          <Box className={classes.filterBar}>
            <TextField
              className={classes.filterInput}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }}
              placeholder="Search program name, key or account..."
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <Select
              className={classes.filterSelect}
              value={accountFilter}
              onChange={(e) => { setAccountFilter(e.target.value); setPage(0) }}
              displayEmpty
              disableUnderline
              MenuProps={{
                classes: { paper: classes.selectPaper },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value="">{ALL_ACCOUNTS_LABEL}</MenuItem>
              {accountNames.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>

            <Box className={classes.filterRight}>
              <Box component="span" className={classes.resultCount}>
                {filteredData.length} of {allData.length} programs
              </Box>
              <Button className={classes.exportPerSiteBtn} onClick={exportPerSiteCsv}>
                <Download />
                <Box paddingLeft='5px'>Export CSV (per site)</Box>
              </Button>
              <Button className={classes.exportBtn} onClick={exportGroupedCsv}>
                <Download />
                <Box paddingLeft='5px'>Export CSV (grouped)</Box>
              </Button>
            </Box>
          </Box>

          <TableData
            classes={classes}
            tableHeadings={tableHeadings}
            tableContents={tableContents}
            isBold={sortBy}
            handleSortChange={handleSortChange}
            renderCellContent={renderCellContent}
            getCellClassName={getCellClassName}
            containerClassName={classes.tableWrap}
            tableClassName={classes.siteIdsTable}
            stickyHeader={false}
            emptyTableText={EMPTY_TABLE_TEXT}
            paginate
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}

export default connect(mapStateToProps)(SiteIdsViewer)
